/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Client Speed Detection Utilities
 * Provides various methods to measure client performance and network speed
 */

// Network Information API types
interface NetworkInformation extends EventTarget {
  downlink: number
  effectiveType: '2g' | '3g' | '4g' | 'slow-2g'
  rtt: number
  saveData: boolean
  type?:
    | 'bluetooth'
    | 'cellular'
    | 'ethernet'
    | 'none'
    | 'wifi'
    | 'wimax'
    | 'other'
    | 'unknown'
}

interface NavigatorWithConnection extends Navigator {
  connection?: NetworkInformation
  mozConnection?: NetworkInformation
  webkitConnection?: NetworkInformation
}

export interface SpeedTestResult {
  downloadSpeed: number // Mbps
  latency: number // ms
  timestamp: number
}

export interface DevicePerformance {
  cpuCores: number
  memory: number // GB
  performanceScore: number // 0-100
}

export interface ConnectionInfo {
  effectiveType: string
  downlink: number // Mbps
  rtt: number // ms
  saveData: boolean
  type?: string
}

/**
 * Get Network Connection Information using Network Information API
 */
export function getConnectionInfo(): ConnectionInfo | null {
  if (typeof navigator === 'undefined') {
    return null
  }

  const nav = navigator as NavigatorWithConnection
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection

  if (!connection) {
    return null
  }

  return {
    effectiveType: connection.effectiveType,
    downlink: connection.downlink,
    rtt: connection.rtt,
    saveData: connection.saveData,
    type: connection.type,
  }
}

/**
 * Get device performance information
 */
export function getDevicePerformance(): DevicePerformance {
  if (typeof navigator === 'undefined') {
    return {
      cpuCores: 1,
      memory: 0,
      performanceScore: 50,
    }
  }

  const cpuCores = navigator.hardwareConcurrency || 1

  // Memory in GB (if available)
  const memory =
    (navigator as any).deviceMemory ||
    (navigator as any).memory?.usedJSHeapSize / (1024 * 1024 * 1024) ||
    0

  // Simple performance scoring based on available metrics
  let performanceScore = 50 // Base score

  // CPU cores factor (0-30 points)
  performanceScore += Math.min(cpuCores * 5, 30)

  // Memory factor (0-20 points)
  if (memory > 0) {
    performanceScore += Math.min(memory * 5, 20)
  }

  return {
    cpuCores,
    memory: Math.round(memory * 100) / 100,
    performanceScore: Math.min(performanceScore, 100),
  }
}

/**
 * Performance-based content delivery recommendations
 */
export function getContentRecommendations(profile: {
  connection: ConnectionInfo | null
  device: DevicePerformance
}) {
  if (typeof navigator === 'undefined') {
    return {
      recommendation: 'medium',
      groupC: {
        defaultDisplay: false,
        shouldLoad: false,
        timer: 0,
        baseTimer: 0,
      },
      groupB: {
        defaultDisplay: false,
        shouldLoad: false,
        timer: 0,
        baseTimer: 0,
      },
      saveData: false,
    }
  }

  const { connection, device } = profile

  const recommendation = !connection
    ? 'medium'
    : connection.effectiveType === '4g' && device.performanceScore >= 70
    ? 'high'
    : connection.effectiveType === 'slow-2g' || device.performanceScore < 40
    ? 'low'
    : ('medium' as 'high' | 'medium' | 'low')

  const recommendations = {
    high: {
      groupC: {
        defaultDisplay: false,
        shouldLoad: false,
        timer: 3000,
        baseTimer: 100,
      },
      groupB: {
        defaultDisplay: false,
        shouldLoad: false,
        timer: 1000,
        baseTimer: 100,
      },
    },
    medium: {
      groupC: {
        defaultDisplay: false,
        shouldLoad: false,
        timer: 0,
        baseTimer: 300,
      },
      groupB: {
        defaultDisplay: false,
        shouldLoad: false,
        timer: 1000,
        baseTimer: 300,
      },
    },
    low: {
      groupC: {
        defaultDisplay: false,
        shouldLoad: false,
        timer: 0,
        baseTimer: 500,
      },
      groupB: {
        defaultDisplay: false,
        shouldLoad: false,
        timer: 0,
        baseTimer: 500,
      },
    },
  }

  return {
    recommendation,
    ...recommendations[recommendation],
    saveData: connection?.saveData || false,
  }
}
